import { Box as MuiBox } from "@mui/material";
import { useTheme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Stack } from "@/Components/v2/atoms/Stack";

import { Divider } from "@/Components/v2/atoms/Divider";

import { getLikelihoodLevel } from "../../helpers/likelihood";
import { LikelihoodProgress } from "./LikelihoodProgress";
import { Typography } from "@/Components/v2/atoms/Typography";

interface LikelihoodSummaryProps {
  likelihoodPercentage: number;
}

export const LikelihoodSummary = ({ likelihoodPercentage }: LikelihoodSummaryProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    const element = containerRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const { level, color, description, recommendation } = getLikelihoodLevel(likelihoodPercentage);

  return (
    <MuiBox
      border={`1px solid ${theme.palette.divider}`}
      p={3}
      mt={3}
      borderRadius={3}
      ref={containerRef}
    >
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
        Scenario Likelihood
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <LikelihoodProgress
        likelihoodPercentage={likelihoodPercentage}
        level={level}
        color={color}
        description={description}
        recommendation={recommendation}
        containerWidth={containerWidth}
      />
      <Divider sx={{ mt: 3 }} />
      <Box pt={3}>
        <Stack direction="column" gap={2}>
          <Typography variant="h6" fontWeight={600}>
            How we calculate Scenario Likelihood
          </Typography>
          <Typography variant="body2">
            A scenario&apos;s likelihood is determined by two factors:
          </Typography>
          <Stack component="ul" direction="column" gap={1.5} sx={{ pl: 2.5, m: 0 }}>
            <Typography component="li" variant="body2">
              <Box component="span" fontWeight={600} pr={1}>
                Change from Historic Spend:
              </Box>
              The greater the change of your total budget and allocation across channels, the harder
              it is to predict the scenario outcome.
            </Typography>
            <Typography component="li" variant="body2">
              <Box component="span" fontWeight={600} pr={1}>
                Confidence Intervals:
              </Box>
              The narrower the confidence interval, the more certain our model is in its prediction.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </MuiBox>
  );
};
